import {
	useState,
	useCallback,
	useMemo,
	useRef,
	useEffect,
	MutableRefObject,
	ReactNode,
} from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Tag, Alert, Radio, message, Collapse, Modal } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	supplierFacilitiesRestCrudThunksForSupplier,
	fetchSupplierServiceAreas,
	updateSupplierServiceAreas,
} from '../../thunks/supplier_facilities_thunks';
import { ServiceArea } from '../../types/serviceArea';
import ServiceAreaMap, { CenterOnTarget } from '../service_area_map/ServiceAreaMap';
import { resolveSearchAreaToFinestBoundary } from '../service_area_map/progressiveGeoBoundariesMap';
import {
	BoundaryRefinementRequest,
	buildDistrictServiceAreasInCountryExcluding,
	buildDistrictServiceAreasInStateExcluding,
	buildRegionServiceAreasInCountryExcluding,
	consolidateProgressiveServiceAreas,
	enrichDistrictServiceAreasWithLabels,
	enrichDistrictServiceAreasWithStateAndCountryLabels,
	enrichRegionServiceAreasWithCountryLabel,
	readGeoJsonSourceFeatures,
	resolveCountryGeometryFromArea,
	resolveParentShapeIdsForArea,
	resolveStateGeometryFromArea,
} from '../service_area_map/progressiveGeoBoundariesMapCore';
import {
	SEL_COUNTRY_LINE,
	SEL_DISTRICT_LINE,
	SEL_REGION_LINE,
	SOURCE_ADM0,
	SOURCE_ADM1,
	SOURCE_ADM2,
} from '../service_area_map/progressiveGeoBoundariesMapConstants';
import MapboxSearchInput from './MapboxSearchInput';
import { SEARCH_GEOCODE_TYPES } from './constants';
import {
	getCountryFromArea,
	getStateFromArea,
	normalizePlaceTypeForMode,
	getInitialServiceAreasFromFacility,
	getCenterOnTarget,
	getCenterOnTargetForAreas,
	formatServiceAreaChipLabel,
	serviceAreaFromApiItem,
	admLevelFromPlaceType,
	USA_DEFAULT_CENTER_ON,
} from './utils';
import { resolveServiceAreaBoundariesFromCDN } from '../../utils/geoBoundariesApi';
import { consolidateServiceAreasFromCdn } from '../../utils/serviceAreaCdnConsolidation';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const style = require('./EditSupplierServiceAreasForm.less');

/** Chips in the left list: wrap inside panel width (no horizontal scroll). */
const SERVICE_AREA_LIST_CHIP_CLASS =
	'box-border !mb-1 max-w-full min-w-0 w-fit break-words whitespace-normal text-left !inline-flex !h-auto !items-center';

/**
 * Solid dot — fill matches progressive map selection line colour for that admin level.
 * A thin white ring sits between the fill and the tab chrome so it stays visible on every tab state.
 */
function GeographyModeSwatch(props: { fillColor: string }): JSX.Element {
	const fill = props.fillColor;
	return (
		<span
			className="editSupplierServiceAreasForm__geoSwatch box-border inline-block h-3 w-3 shrink-0 rounded-full border border-solid border-white"
			style={{
				backgroundColor: fill,
			}}
			aria-hidden
		/>
	);
}
const MAP_IDLE_FALLBACK_TIMEOUT_MS = 15000;

/** Stable fingerprint of which boundaries are selected (for dirty check vs last saved / initial load). */
function serviceAreasSelectionSignature(areas: ServiceArea[] | null | undefined): string {
	const list = areas || [];
	const rows = list.map((a) => ({
		bid:
			a.boundaryShapeId != null && a.boundaryShapeId !== undefined ? String(a.boundaryShapeId) : '',
		bg:
			a.boundaryShapeGroup != null && a.boundaryShapeGroup !== undefined
				? String(a.boundaryShapeGroup)
				: '',
		pt: a.placeType ? String(a.placeType) : '',
		n: a.name ? String(a.name) : '',
	}));
	rows.sort((x, y) => {
		const byId = x.bid.localeCompare(y.bid);
		if (byId !== 0) {
			return byId;
		}
		return x.n.localeCompare(y.n);
	});
	return JSON.stringify(rows);
}

const SERVICE_AREAS_MODAL_WARNING_ICON = (
	<ExclamationCircleOutlined
		className="relative top-0.5 align-top text-[22px] text-[#faad14]"
		aria-hidden
	/>
);

/**
 * Single entry point for every confirmation modal in this form. Uses the standard AntD
 * `Modal.confirm` API with consistent defaults so all modals look identical — centered on the
 * viewport, warning icon, primary-styled OK, no X close button (dismissal is via the Cancel
 * button, matching standard AntD confirm dialog conventions and avoiding the X-misalignment
 * that the older wrapClassName overrides created).
 */
function showServiceAreasConfirm(config: {
	title: ReactNode;
	content?: ReactNode;
	okText?: string;
	cancelText?: string;
	okType?: 'primary' | 'default' | 'danger';
	width?: number;
	onOk?: () => void | Promise<void>;
}): void {
	Modal.confirm({
		title: config.title,
		content: config.content,
		icon: SERVICE_AREAS_MODAL_WARNING_ICON,
		okText: config.okText || 'Confirm',
		cancelText: config.cancelText || 'Cancel',
		okType: config.okType || 'primary',
		centered: true,
		width: config.width || 480,
		onOk: config.onOk,
	});
}

/**
 * Map search-resolved `placeType` to the progressive map tab (`country` | `region` | `district`).
 * State-level hits use the State tab; county-level use County; everything else defaults to County
 * (postcodes, localities, etc.) so the UI matches the usual fine-picking flow.
 */
function mapSearchResultPlaceTypeToFormMode(placeType: string | undefined): string {
	const normalizedPlaceType = placeType ? String(placeType) : '';
	if (normalizedPlaceType === 'country') {
		return 'country';
	}
	if (normalizedPlaceType === 'region' || normalizedPlaceType === 'state') {
		return 'region';
	}
	if (normalizedPlaceType === 'district') {
		return 'district';
	}
	return 'district';
}

/** After exploding a whole-country or whole-state row, append finer areas without duplicate `boundaryShapeId`s. */
function mergeServiceAreasReplacingParent(
	current: ServiceArea[],
	parentMatches: (area: ServiceArea) => boolean,
	newChildAreas: ServiceArea[]
): ServiceArea[] {
	const withoutParent = current.filter((a) => !parentMatches(a));
	const existingIds: Record<string, boolean> = {};
	for (let i = 0; i < withoutParent.length; i++) {
		const id = withoutParent[i].boundaryShapeId;
		if (id) {
			existingIds[String(id)] = true;
		}
	}
	const toAppend: ServiceArea[] = [];
	for (let j = 0; j < newChildAreas.length; j++) {
		const item = newChildAreas[j];
		const bid = item.boundaryShapeId;
		if (!bid) {
			continue;
		}
		if (existingIds[String(bid)]) {
			continue;
		}
		toAppend.push(item);
	}
	return ([] as ServiceArea[]).concat(withoutParent as any, toAppend as any);
}

function withProgressiveConsolidation(
	mapRef: MutableRefObject<any>,
	list: ServiceArea[],
	getState: (a: ServiceArea) => string,
	getCountry: (a: ServiceArea) => string
): ServiceArea[] {
	const mapInstance = mapRef.current;
	if (!mapInstance) {
		return list;
	}
	return consolidateProgressiveServiceAreas(mapInstance, list, getState, getCountry);
}

interface EditSupplierServiceAreasFormProps {
	loading: boolean;
	updateSupplier: any;
	fetchSupplierFacility: (id: string) => Promise<any>;
	fetchServiceAreas: (supplierFacilityId: string | number) => Promise<any>;
	updateServiceAreas: (supplierFacilityId: string | number, serviceAreas: any[]) => Promise<any>;
	successCallback: any;
	cancelCallback: any;
	supplierFacility: any;
	errors: string[];
	/** When true, map and list are view-only (no save/cancel, search, or edits). */
	readOnly?: boolean;
}

const EditSupplierServiceAreasForm = (props: EditSupplierServiceAreasFormProps) => {
	const {
		loading,
		updateSupplier,
		fetchSupplierFacility,
		fetchServiceAreas,
		updateServiceAreas,
		successCallback,
		cancelCallback,
		supplierFacility,
		errors,
		readOnly,
	} = props;
	const isReadOnly = readOnly === true;

	const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
	const [mode, setMode] = useState('country');
	const [focusedState, setFocusedState] = useState<ServiceArea | null>(null);
	const [centerOn, setCenterOn] = useState<CenterOnTarget | null>(null);
	const progressiveMapRef = useRef<any>(null);
	const initialCenterSetRef = useRef(false);
	const [openCountryPanels, setOpenCountryPanels] = useState<string[]>([]);
	const [searchPreviewServiceArea, setSearchPreviewServiceArea] = useState<ServiceArea | null>(
		null
	);
	const [searchText, setSearchText] = useState('');
	const [isSavingServiceAreas, setIsSavingServiceAreas] = useState(false);
	const seenCountriesRef = useRef<Set<string>>(new Set());
	const baselineSelectionSignatureRef = useRef<string>(serviceAreasSelectionSignature([]));

	useMountEffect(() => {
		if (!supplierFacility || !supplierFacility.id) return;
		fetchServiceAreas(supplierFacility.id)
			.then((response: any) => {
				const json = (response && response.json) || {};
				const items: any[] = Array.isArray(json) ? json : Array.isArray(json.data) ? json.data : [];
				const areas = items.map(serviceAreaFromApiItem).filter(Boolean) as ServiceArea[];
				setServiceAreas(areas);
				baselineSelectionSignatureRef.current = serviceAreasSelectionSignature(areas);
				if (areas.length === 0) {
					setCenterOn(USA_DEFAULT_CENTER_ON);
					initialCenterSetRef.current = true;
				} else {
					const center = getCenterOnTargetForAreas(areas);
					if (center) {
						setCenterOn(center);
						initialCenterSetRef.current = true;
					}
					// else: CDN hydration effect will set it once boundaries arrive
				}
			})
			.catch(() => {
				const fallback = getInitialServiceAreasFromFacility(supplierFacility);
				setServiceAreas(fallback);
				baselineSelectionSignatureRef.current = serviceAreasSelectionSignature(fallback);
				if (fallback.length === 0) {
					setCenterOn(USA_DEFAULT_CENTER_ON);
					initialCenterSetRef.current = true;
				} else {
					const center = getCenterOnTargetForAreas(fallback);
					if (center) {
						setCenterOn(center);
						initialCenterSetRef.current = true;
					}
				}
			});
	});

	// Hydrate boundary geometries from CDN whenever un-hydrated areas are present. The backend GET
	// response doesn't include geometry — it carries `boundaryShapeId` + `boundaryShapeGroup` +
	// `placeType`, from which `resolveServiceAreaBoundariesFromCDN` derives the `{ISO}-{ADM}.geojson`
	// URL. Self-limiting via the `needsResolve` guard: once every area has `boundaries`, the effect
	// runs but short-circuits — no infinite loop when `setServiceAreas` re-fires it.
	useEffect(() => {
		let cancelled = false;
		const needsResolve = serviceAreas.some((serviceArea) => {
			return (
				!serviceArea.boundaries && serviceArea.boundaryShapeId && serviceArea.boundaryShapeGroup
			);
		});
		if (!needsResolve) return;
		resolveServiceAreaBoundariesFromCDN(serviceAreas.slice()).then((resolved) => {
			if (cancelled) return;
			// Merge resolved boundaries into current state to avoid racing user edits.
			const boundaryKeyToGeometry = new Map<string, any>();
			(resolved || []).forEach((area) => {
				if (!area) return;
				if (!area.boundaryShapeId || !area.boundaryShapeGroup) return;
				if (!area.boundaries) return;
				boundaryKeyToGeometry.set(
					`${String(area.boundaryShapeGroup)}::${String(area.boundaryShapeId)}`,
					area.boundaries
				);
			});
			if (boundaryKeyToGeometry.size === 0) return;
			if (!initialCenterSetRef.current) {
				const center = getCenterOnTargetForAreas(resolved as ServiceArea[]);
				if (center) {
					setCenterOn(center);
					initialCenterSetRef.current = true;
				}
			}
			setServiceAreas((prev) => {
				return (prev || []).map((area) => {
					if (!area) return area;
					if (area.boundaries) return area;
					if (!area.boundaryShapeId || !area.boundaryShapeGroup) return area;
					const boundaryShapeKey = `${String(area.boundaryShapeGroup)}::${String(
						area.boundaryShapeId
					)}`;
					const boundaries = boundaryKeyToGeometry.get(boundaryShapeKey);
					return boundaries ? { ...area, boundaries } : area;
				});
			});
		});
		return () => {
			cancelled = true;
		};
	}, [serviceAreas]);

	// CDN-backed fallback consolidation: collapse N-of-N children → parent independent of Mapbox ADM load state.
	useEffect(() => {
		let cancelled = false;
		consolidateServiceAreasFromCdn(serviceAreas, getStateFromArea, getCountryFromArea).then(
			(consolidated) => {
				if (cancelled) return;
				if (consolidated !== serviceAreas && consolidated.length < serviceAreas.length) {
					setServiceAreas(consolidated);
				}
			}
		);
		return () => {
			cancelled = true;
		};
	}, [serviceAreas]);

	const handleRemove = useCallback(
		(index: number) => {
			setSearchPreviewServiceArea(null);
			const areaToRemove = serviceAreas[index];
			if (!areaToRemove) return;

			const removeState = getStateFromArea(areaToRemove);
			const removeCountry = getCountryFromArea(areaToRemove);

			const nextAreas = serviceAreas.filter((area, i) => {
				if (i === index) return false;

				if (
					areaToRemove.placeType === 'region' &&
					area.placeType === 'district' &&
					getStateFromArea(area) === removeState &&
					getCountryFromArea(area) === removeCountry
				) {
					return false;
				}

				if (areaToRemove.placeType === 'country' && getCountryFromArea(area) === removeCountry) {
					return false;
				}

				return true;
			});

			setServiceAreas(
				withProgressiveConsolidation(
					progressiveMapRef,
					nextAreas,
					getStateFromArea,
					getCountryFromArea
				)
			);
		},
		[serviceAreas]
	);

	const confirmRemoveAllForCountry = useCallback((countryDisplayName: string) => {
		showServiceAreasConfirm({
			title: 'Are you sure you want to remove all entries for ' + countryDisplayName + '?',
			okText: 'Confirm',
			cancelText: 'Cancel',
			width: 520,
			onOk: () => {
				setSearchPreviewServiceArea(null);
				setServiceAreas((prev) =>
					withProgressiveConsolidation(
						progressiveMapRef,
						prev.filter(
							(area) => (getCountryFromArea(area) || 'Unspecified Country') !== countryDisplayName
						),
						getStateFromArea,
						getCountryFromArea
					)
				);
				setOpenCountryPanels((prev) => prev.filter((key) => key !== countryDisplayName));
			},
		});
	}, []);

	const handleFooterCancelClick = useCallback(() => {
		if (serviceAreasSelectionSignature(serviceAreas) === baselineSelectionSignatureRef.current) {
			cancelCallback();
			return;
		}
		showServiceAreasConfirm({
			title: 'Discard changes?',
			content: 'All unsaved changes will be lost.',
			cancelText: 'Continue Editing',
			okText: 'Discard',
			onOk: () => {
				cancelCallback();
			},
		});
	}, [serviceAreas, cancelCallback]);

	const handleAdd = useCallback(
		(area: ServiceArea, addOpts?: { fromSearch?: boolean }) => {
			setSearchPreviewServiceArea(null);
			setSearchText('');
			const fromSearch = addOpts && addOpts.fromSearch === true;
			const placeTypeForAdd = (() => {
				if (fromSearch && area.boundaryShapeId) {
					return area.placeType;
				}
				return normalizePlaceTypeForMode(area.placeType, mode);
			})();
			const normalizedArea = {
				...area,
				placeType: placeTypeForAdd,
			};
			const placeType = normalizedArea.placeType;
			const selectedCountry = getCountryFromArea(normalizedArea);
			const selectedState = getStateFromArea(normalizedArea);

			if (placeType !== 'country' && placeType !== 'region' && placeType !== 'district') {
				message.info(
					'Please select a country, state, or county — or search by ZIP / postal code to jump to that area.'
				);
				return;
			}

			// --- Search dropdown: same geography as an existing row → remove it (toggle off) ---
			if (fromSearch) {
				let removeIndex = -1;
				if (normalizedArea.boundaryShapeId) {
					removeIndex = serviceAreas.findIndex(
						(existingArea) =>
							existingArea.boundaryShapeId != null &&
							String(existingArea.boundaryShapeId) === String(normalizedArea.boundaryShapeId)
					);
				}
				if (removeIndex < 0 && normalizedArea.name) {
					removeIndex = serviceAreas.findIndex(
						(existingArea) => existingArea.name === normalizedArea.name
					);
				}
				if (removeIndex >= 0) {
					handleRemove(removeIndex);
					return;
				}
			}

			// --- Duplicate check (map pick: still block with a hint) ---
			if (!fromSearch) {
				if (normalizedArea.boundaryShapeId) {
					const dupByBoundary = serviceAreas.some(
						(existingArea) =>
							existingArea.boundaryShapeId &&
							existingArea.boundaryShapeId === normalizedArea.boundaryShapeId
					);
					if (dupByBoundary) {
						message.info('Already selected.');
						return;
					}
				}
				const existsAlready = serviceAreas.some(
					(existingArea) => existingArea.name === normalizedArea.name
				);
				if (existsAlready) {
					message.info('Already selected.');
					return;
				}
			}

			// --- Hierarchy: parent blocks child selection ---
			if (placeType === 'region' || placeType === 'district') {
				const hasCountrySelected = serviceAreas.some(
					(existing) =>
						existing.placeType === 'country' && getCountryFromArea(existing) === selectedCountry
				);
				if (hasCountrySelected) {
					message.warning(
						'Country already selected. You cannot select states or counties within it.'
					);
					return;
				}
			}

			if (placeType === 'district') {
				const hasStateSelected = serviceAreas.some(
					(existing) =>
						existing.placeType === 'region' &&
						getStateFromArea(existing) === selectedState &&
						getCountryFromArea(existing) === selectedCountry
				);
				if (hasStateSelected) {
					message.warning('State already selected. You cannot select counties within it.');
					return;
				}
			}

			// --- Parent overrides children: selecting parent removes children ---
			if (placeType === 'country') {
				const hasStateSelectionsInCountry = serviceAreas.some(
					(existing) =>
						existing.placeType === 'region' && getCountryFromArea(existing) === selectedCountry
				);
				const hasDistrictSelectionsInCountry = serviceAreas.some(
					(existing) =>
						existing.placeType === 'district' && getCountryFromArea(existing) === selectedCountry
				);
				const commitCountrySelection = () => {
					const nextAreas = serviceAreas.filter(
						(existing) => getCountryFromArea(existing) !== selectedCountry
					);
					const mergedCountry = ([] as ServiceArea[]).concat(
						nextAreas as any,
						[normalizedArea] as any
					);
					setServiceAreas(
						withProgressiveConsolidation(
							progressiveMapRef,
							mergedCountry,
							getStateFromArea,
							getCountryFromArea
						)
					);
					if (fromSearch) {
						setCenterOn(getCenterOnTarget(normalizedArea, 4));
					}
				};
				if (hasStateSelectionsInCountry || hasDistrictSelectionsInCountry) {
					const clearedKindsTitle =
						hasStateSelectionsInCountry && hasDistrictSelectionsInCountry
							? 'State and County'
							: hasStateSelectionsInCountry
							? 'State'
							: 'County';
					const clearedKindsBody =
						hasStateSelectionsInCountry && hasDistrictSelectionsInCountry
							? 'states and counties'
							: hasStateSelectionsInCountry
							? 'states'
							: 'counties';
					showServiceAreasConfirm({
						title: `Proceed to clear ${clearedKindsTitle} selections?`,
						content: `Selecting the country will clear the ${clearedKindsBody} you've already picked. Do you want to proceed?`,
						cancelText: 'Cancel',
						okText: 'Proceed',
						width: 520,
						onOk: () => {
							commitCountrySelection();
						},
					});
					return;
				}
				commitCountrySelection();
				return;
			}

			if (placeType === 'region') {
				const hasDistrictsInState = serviceAreas.some(
					(existing) =>
						existing.placeType === 'district' &&
						getStateFromArea(existing) === selectedState &&
						getCountryFromArea(existing) === selectedCountry
				);
				const commitRegionSelection = () => {
					const nextAreas = serviceAreas.filter((existing) => {
						if (
							existing.placeType === 'district' &&
							getStateFromArea(existing) === selectedState &&
							getCountryFromArea(existing) === selectedCountry
						) {
							return false;
						}
						return true;
					});
					const mergedRegion = ([] as ServiceArea[]).concat(
						nextAreas as any,
						[normalizedArea] as any
					);
					setServiceAreas(
						withProgressiveConsolidation(
							progressiveMapRef,
							mergedRegion,
							getStateFromArea,
							getCountryFromArea
						)
					);
					if (fromSearch) {
						setCenterOn(getCenterOnTarget(normalizedArea));
					}
				};
				if (hasDistrictsInState) {
					showServiceAreasConfirm({
						title: 'Proceed to clear County selections?',
						content: `Selecting the state will clear the counties you've already picked. Do you want to proceed?`,
						cancelText: 'Cancel',
						okText: 'Proceed',
						width: 520,
						onOk: () => {
							commitRegionSelection();
						},
					});
					return;
				}
				commitRegionSelection();
				return;
			}

			// --- County: just add ---
			const mergedDistrict = ([] as ServiceArea[]).concat(
				serviceAreas as any,
				[normalizedArea] as any
			);
			setServiceAreas(
				withProgressiveConsolidation(
					progressiveMapRef,
					mergedDistrict,
					getStateFromArea,
					getCountryFromArea
				)
			);
			if (fromSearch) {
				setCenterOn(getCenterOnTarget(normalizedArea));
			}
		},
		[serviceAreas, mode, handleRemove]
	);

	const handleApplyBoundaryRefinement = useCallback((request: BoundaryRefinementRequest) => {
		const map = progressiveMapRef.current;
		if (!map) {
			return;
		}
		const excludeId =
			request.childArea.boundaryShapeId != null ? String(request.childArea.boundaryShapeId) : '';
		if (!excludeId) {
			return;
		}
		if (request.kind === 'state_to_districts_excluding_child') {
			const stateArea = request.parentArea;
			const stateGeom = resolveStateGeometryFromArea(map, stateArea, SOURCE_ADM1);
			if (!stateGeom) {
				message.warning(
					'Could not resolve the state outline. Zoom until boundaries load, then try again.'
				);
				return;
			}
			const adm2Features = readGeoJsonSourceFeatures(map, SOURCE_ADM2);
			if (!adm2Features || adm2Features.length === 0) {
				message.warning(
					'County boundaries are not loaded yet. Zoom in on the state and try again.'
				);
				return;
			}
			const newDistricts = buildDistrictServiceAreasInStateExcluding(
				adm2Features,
				stateGeom,
				excludeId
			);
			if (newDistricts.length === 0) {
				message.warning('No counties found for that state on the map. Try zooming in.');
				return;
			}
			const stateName = getStateFromArea(stateArea);
			const countryName = getCountryFromArea(stateArea);
			const labeled = enrichDistrictServiceAreasWithLabels(newDistricts, stateName, countryName);
			setServiceAreas((prev) => {
				const merged = mergeServiceAreasReplacingParent(
					prev,
					(a) =>
						a.placeType === 'region' &&
						a.boundaryShapeId &&
						stateArea.boundaryShapeId &&
						String(a.boundaryShapeId) === String(stateArea.boundaryShapeId),
					labeled
				);
				return withProgressiveConsolidation(
					progressiveMapRef,
					merged,
					getStateFromArea,
					getCountryFromArea
				);
			});
			return;
		}
		if (request.kind === 'country_to_regions_excluding_child') {
			const countryArea = request.parentArea;
			const countryGeom = resolveCountryGeometryFromArea(map, countryArea, SOURCE_ADM0);
			if (!countryGeom) {
				message.warning(
					'Could not resolve the country outline. Zoom until boundaries load, then try again.'
				);
				return;
			}
			const adm1Features = readGeoJsonSourceFeatures(map, SOURCE_ADM1);
			if (!adm1Features || adm1Features.length === 0) {
				message.warning(
					'State boundaries are not loaded yet. Zoom in on the country and try again.'
				);
				return;
			}
			const newRegions = buildRegionServiceAreasInCountryExcluding(
				adm1Features,
				countryGeom,
				excludeId,
				countryArea
			);
			if (newRegions.length === 0) {
				message.warning('No states found for that country on the map. Try zooming in.');
				return;
			}
			const countryName = getCountryFromArea(countryArea);
			const labeled = enrichRegionServiceAreasWithCountryLabel(newRegions, countryName);
			setServiceAreas((prev) => {
				const merged = mergeServiceAreasReplacingParent(
					prev,
					(a) =>
						a.placeType === 'country' &&
						a.boundaryShapeId &&
						countryArea.boundaryShapeId &&
						String(a.boundaryShapeId) === String(countryArea.boundaryShapeId),
					labeled
				);
				return withProgressiveConsolidation(
					progressiveMapRef,
					merged,
					getStateFromArea,
					getCountryFromArea
				);
			});
			return;
		}
		if (request.kind === 'country_to_districts_excluding_child') {
			const countryArea = request.parentArea;
			const countryGeom = resolveCountryGeometryFromArea(map, countryArea, SOURCE_ADM0);
			if (!countryGeom) {
				message.warning(
					'Could not resolve the country outline. Zoom until boundaries load, then try again.'
				);
				return;
			}
			const adm2Features = readGeoJsonSourceFeatures(map, SOURCE_ADM2);
			if (!adm2Features || adm2Features.length === 0) {
				message.warning(
					'County boundaries are not loaded yet. Zoom in on the country and try again.'
				);
				return;
			}
			const newDistricts = buildDistrictServiceAreasInCountryExcluding(
				adm2Features,
				countryGeom,
				excludeId,
				countryArea
			);
			if (newDistricts.length === 0) {
				message.warning('No counties found for that country on the map. Try zooming in.');
				return;
			}
			const adm1Features = readGeoJsonSourceFeatures(map, SOURCE_ADM1);
			const countryName = getCountryFromArea(countryArea);
			const labeled = enrichDistrictServiceAreasWithStateAndCountryLabels(
				newDistricts,
				adm1Features,
				countryName
			);
			setServiceAreas((prev) => {
				const merged = mergeServiceAreasReplacingParent(
					prev,
					(a) =>
						a.placeType === 'country' &&
						a.boundaryShapeId &&
						countryArea.boundaryShapeId &&
						String(a.boundaryShapeId) === String(countryArea.boundaryShapeId),
					labeled
				);
				return withProgressiveConsolidation(
					progressiveMapRef,
					merged,
					getStateFromArea,
					getCountryFromArea
				);
			});
		}
	}, []);

	const handleSearchSelect = useCallback(
		async (area: ServiceArea) => {
			if (!progressiveMapRef.current) {
				handleAdd(area, { fromSearch: true });
				return;
			}
			const progressiveMapInstance = progressiveMapRef.current;
			if (area.boundaryShapeId) {
				let previewWithGeometry: ServiceArea = area;
				if (!area.boundaries && area.boundaryShapeGroup) {
					const fromCdn = await resolveServiceAreaBoundariesFromCDN([area]);
					if (fromCdn && fromCdn[0]) {
						previewWithGeometry = fromCdn[0];
					}
				}
				const zoomHeuristic =
					previewWithGeometry.placeType === 'country'
						? 4
						: previewWithGeometry.placeType === 'district'
						? 9
						: 7;
				const centerTarget = getCenterOnTarget(previewWithGeometry, zoomHeuristic);
				if (centerTarget) {
					setCenterOn(centerTarget);
				}
				setFocusedState(null);
				setMode(mapSearchResultPlaceTypeToFormMode(previewWithGeometry.placeType));
				setSearchPreviewServiceArea(previewWithGeometry);
				return;
			}
			if (area.center && area.center.length === 2) {
				const isPostcodePick = area.placeType === 'postcode';
				const minZoomForPick = isPostcodePick ? 10 : 8;
				progressiveMapInstance.jumpTo({
					center: [area.center[0], area.center[1]],
					zoom: Math.max(minZoomForPick, progressiveMapInstance.getZoom()),
				});
				await new Promise<void>((resolve) => {
					let settled = false;
					const onIdle = () => {
						if (settled) return;
						settled = true;
						clearTimeout(timeoutId);
						resolve();
					};
					const timeoutId = setTimeout(() => {
						if (settled) return;
						settled = true;
						try {
							if (progressiveMapInstance && progressiveMapInstance.off) {
								progressiveMapInstance.off('idle', onIdle);
							}
						} catch (ignoredError) {}
						resolve();
					}, MAP_IDLE_FALLBACK_TIMEOUT_MS);
					progressiveMapInstance.once('idle', onIdle);
				});
			}
			const resolved = resolveSearchAreaToFinestBoundary(progressiveMapInstance, area);
			if (!resolved.boundaryShapeId && area.placeType === 'postcode') {
				setSearchPreviewServiceArea(null);
				message.warning(
					'Could not match that postal code to a boundary on the map. Try zooming in, or pick the area on the map.'
				);
				return;
			}
			if (!resolved.boundaryShapeId) {
				setSearchPreviewServiceArea(null);
				message.info(
					'Zoom in on the map and click the boundary to add it, or try a more specific search.'
				);
				return;
			}
			let withBoundaries = resolved;
			if (!resolved.boundaries && resolved.boundaryShapeGroup) {
				const fromCdn = await resolveServiceAreaBoundariesFromCDN([resolved]);
				if (fromCdn && fromCdn[0]) {
					withBoundaries = fromCdn[0];
				}
			}
			const zoomHeuristic =
				withBoundaries.placeType === 'country'
					? 4
					: withBoundaries.placeType === 'district'
					? 9
					: 7;
			const centerTarget = getCenterOnTarget(withBoundaries, zoomHeuristic);
			if (centerTarget) {
				setCenterOn(centerTarget);
			}
			setFocusedState(null);
			setMode(mapSearchResultPlaceTypeToFormMode(withBoundaries.placeType));
			setSearchPreviewServiceArea(withBoundaries);
		},
		[handleAdd]
	);

	const handleRemoveBoundaryClick = useCallback(
		(area: ServiceArea) => {
			const id = area.boundaryShapeId != null ? String(area.boundaryShapeId) : '';
			if (!id) {
				return;
			}
			const idx = serviceAreas.findIndex(
				(a) => a.boundaryShapeId != null && String(a.boundaryShapeId) === id
			);
			if (idx >= 0) {
				handleRemove(idx);
			}
		},
		[serviceAreas, handleRemove]
	);

	const handleCenterOnArea = useCallback((area: ServiceArea) => {
		if (area.bbox) {
			setCenterOn({ bbox: area.bbox });
		} else if (area.center && (area.center[0] !== 0 || area.center[1] !== 0)) {
			const zoom = area.placeType === 'country' ? 4 : area.placeType === 'district' ? 9 : 7;
			setCenterOn({ center: area.center, zoom: zoom });
		}
	}, []);

	/**
	 * Left-list chip click: center the map, switch the Country / State / County tab to match that row,
	 * and show the same neutral search-preview outline as the search bar (see `searchPreviewServiceArea`).
	 */
	const handleSelectedChipClick = useCallback(
		async (area: ServiceArea) => {
			if (isReadOnly) {
				handleCenterOnArea(area);
				return;
			}
			setFocusedState(null);
			setMode(mapSearchResultPlaceTypeToFormMode(area.placeType));
			if (area.boundaryShapeId) {
				let previewWithGeometry: ServiceArea = area;
				if (!area.boundaries && area.boundaryShapeGroup) {
					const fromCdn = await resolveServiceAreaBoundariesFromCDN([area]);
					if (fromCdn && fromCdn[0]) {
						previewWithGeometry = fromCdn[0];
					}
				}
				const zoomHeuristic =
					previewWithGeometry.placeType === 'country'
						? 4
						: previewWithGeometry.placeType === 'district'
						? 9
						: 7;
				const centerTarget = getCenterOnTarget(previewWithGeometry, zoomHeuristic);
				if (centerTarget) {
					setCenterOn(centerTarget);
				}
				setSearchPreviewServiceArea(previewWithGeometry);
				return;
			}
			handleCenterOnArea(area);
			setSearchPreviewServiceArea(null);
		},
		[isReadOnly, handleCenterOnArea]
	);

	const handleSubmit = useCallback(() => {
		const progressiveMap = progressiveMapRef.current;
		const cachedSources = progressiveMap
			? {
					adm0Features: readGeoJsonSourceFeatures(progressiveMap, SOURCE_ADM0),
					adm1Features: readGeoJsonSourceFeatures(progressiveMap, SOURCE_ADM1),
					adm2Features: readGeoJsonSourceFeatures(progressiveMap, SOURCE_ADM2),
			  }
			: undefined;
		const normalizeOutboundPlaceType = (rawPlaceType: string): string =>
			rawPlaceType === 'state' ? 'region' : rawPlaceType;
		const emptyToNull = (value: string | null | undefined): string | null => {
			if (value === undefined || value === null) return null;
			const trimmed = String(value).trim();
			return trimmed.length > 0 ? trimmed : null;
		};
		const pickParentShapeId = (
			resolvedValue: string | null | undefined,
			existingValue: string | null | undefined
		): string | null => {
			if (resolvedValue !== undefined) return resolvedValue;
			if (existingValue !== undefined && existingValue !== null) return existingValue;
			return null;
		};

		const serviceAreaPayload = serviceAreas
			.filter((area) => {
				const placeType = normalizeOutboundPlaceType(area.placeType ? String(area.placeType) : '');
				const isBoundaryLevel =
					placeType === 'country' || placeType === 'region' || placeType === 'district';
				return isBoundaryLevel;
			})
			.map((area) => {
				const resolvedParents = progressiveMap
					? resolveParentShapeIdsForArea(progressiveMap, area, cachedSources)
					: {};
				const placeType = normalizeOutboundPlaceType(String(area.placeType));
				return {
					supplierFacilityId: Number(supplierFacility.id),
					boundaryShapeId: area.boundaryShapeId != null ? String(area.boundaryShapeId) : undefined,
					boundaryShapeGroup:
						area.boundaryShapeGroup != null ? String(area.boundaryShapeGroup) : undefined,
					admLevel: admLevelFromPlaceType(placeType),
					placeType,
					parentStateShapeId: pickParentShapeId(
						resolvedParents.parentStateShapeId,
						area.parentStateShapeId
					),
					parentCountryShapeId: pickParentShapeId(
						resolvedParents.parentCountryShapeId,
						area.parentCountryShapeId
					),
					displayName: area.name,
					countryName: emptyToNull(getCountryFromArea(area)),
					stateName: emptyToNull(getStateFromArea(area)),
				};
			});

		setIsSavingServiceAreas(true);
		updateServiceAreas(supplierFacility.id, serviceAreaPayload)
			.then((updateResponse) => {
				setSearchPreviewServiceArea(null);
				baselineSelectionSignatureRef.current = serviceAreasSelectionSignature(serviceAreas);
				message.success('Service areas updated successfully.');
				const facilityId = supplierFacility.id;
				if (facilityId && fetchSupplierFacility) {
					return fetchSupplierFacility(facilityId).then(() => {
						successCallback(updateResponse);
					});
				} else {
					successCallback(updateResponse);
				}
			})
			.catch(() => {
				message.error('Failed to update service areas. Please try again.');
			})
			.finally(() => {
				setIsSavingServiceAreas(false);
			});
	}, [supplierFacility, serviceAreas, updateServiceAreas, fetchSupplierFacility, successCallback]);

	const mapGeocodeTypes = useMemo(() => {
		if (mode === 'district') {
			return 'district,place,locality';
		}
		if (mode === 'country') {
			return 'country';
		}
		return 'country,region';
	}, [mode]);

	const countryGroups = useMemo(() => {
		const grouped = serviceAreas.reduce((accumulator, area) => {
			const countryName = getCountryFromArea(area) || 'Unspecified Country';
			if (!accumulator[countryName]) {
				accumulator[countryName] = {
					countries: [],
					states: [],
					counties: [],
				};
			}
			if (area.placeType === 'country') {
				accumulator[countryName].countries.push(area);
			} else if (area.placeType === 'region') {
				accumulator[countryName].states.push(area);
			} else if (area.placeType === 'district') {
				accumulator[countryName].counties.push(area);
			}
			return accumulator;
		}, {} as any);

		return Object.keys(grouped)
			.sort((leftCountryName, rightCountryName) => leftCountryName.localeCompare(rightCountryName))
			.map((countryName) => ({
				country: countryName,
				...grouped[countryName],
			}));
	}, [serviceAreas]);

	// Open any country panel the first time it appears (initial load + newly added)
	// without overriding the user's later collapse/expand choices.
	useEffect(() => {
		const currentNames = countryGroups.map((group) => group.country);
		const seen = seenCountriesRef.current;
		const newlyAdded: string[] = [];
		for (let i = 0; i < currentNames.length; i++) {
			if (!seen.has(currentNames[i])) {
				seen.add(currentNames[i]);
				newlyAdded.push(currentNames[i]);
			}
		}
		if (newlyAdded.length === 0) return;
		setOpenCountryPanels((prev) => {
			const next = prev.slice();
			for (let i = 0; i < newlyAdded.length; i++) {
				if (next.indexOf(newlyAdded[i]) < 0) {
					next.push(newlyAdded[i]);
				}
			}
			return next;
		});
	}, [countryGroups]);

	return (
		<div className="editSupplierServiceAreasForm flex h-full min-h-0 flex-col">
			<h3 className="editSupplierServiceAreasForm__title shrink-0">
				Select the service areas your company serves
			</h3>

			{errors && errors.length > 0 && (
				<Alert message={errors.join(', ')} type="error" className="mb-4" />
			)}

			<div className="flex min-h-0 flex-1 gap-5">
				{/* Left panel — selected areas list */}
				<div className="flex w-[280px] min-w-0 max-w-[280px] shrink-0 flex-col overflow-hidden border-r border-grey-4 pr-4">
					<div className="h5medium mb-3">Selected Service Areas ({serviceAreas.length})</div>

					<div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
						{serviceAreas.length === 0 ? (
							<div className="pt-10 text-center text-[13px] text-character-disabled">
								{isReadOnly
									? 'No service areas configured.'
									: 'Search or click the map to select Country, State, or County'}
							</div>
						) : (
							<Collapse
								bordered={false}
								activeKey={openCountryPanels}
								onChange={(keys) => {
									const next = Array.isArray(keys) ? keys : [keys];
									setOpenCountryPanels(next as string[]);
								}}
								className="min-w-0 max-w-full !bg-transparent [&_.ant-collapse-content-box]:!min-w-0 [&_.ant-collapse-content-box]:!max-w-full [&_.ant-collapse-content-box]:!overflow-x-hidden [&_.ant-collapse-content-box]:!px-0 [&_.ant-collapse-content-box]:!pb-2 [&_.ant-collapse-content-box]:!pt-1 [&_.ant-collapse-content]:!border-0 [&_.ant-collapse-header]:!px-0 [&_.ant-collapse-header]:!py-1 [&_.ant-collapse-item:last-child]:!border-b-0 [&_.ant-collapse-item]:!border-b [&_.ant-collapse-item]:!border-grey-4"
							>
								{countryGroups.map((group) => {
									const entriesForCountry =
										group.countries.length + group.states.length + group.counties.length;
									const showClearCountry = !isReadOnly && entriesForCountry > 1;
									return (
										<Collapse.Panel
											key={group.country}
											header={
												<div className="flex h-6 w-full min-w-0 max-w-full items-center justify-between gap-2 pr-0.5">
													<span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase tracking-[0.5px] text-character-secondary">
														{group.country}
													</span>
													{showClearCountry ? (
														<Button
															type="text"
															size="small"
															className="shrink-0 !px-1 !py-0 text-[11px] text-character-secondary hover:text-character-primary"
															aria-label={
																'Clear ' + entriesForCountry + ' selections for ' + group.country
															}
															onClick={(clickEvent) => {
																clickEvent.stopPropagation();
																confirmRemoveAllForCountry(group.country);
															}}
														>
															{'Clear (' + entriesForCountry + ')'}
														</Button>
													) : null}
												</div>
											}
										>
											<div className="flex min-w-0 max-w-full flex-col flex-wrap gap-x-1 gap-y-1 [&_.ant-tag]:!h-auto [&_.ant-tag]:!whitespace-normal">
												{group.countries.map((area) => {
													const areaIndex = serviceAreas.indexOf(area);
													return (
														<Tag
															key={'country-' + areaIndex}
															closable={!isReadOnly}
															onClose={isReadOnly ? undefined : () => handleRemove(areaIndex)}
															onClick={() => handleSelectedChipClick(area)}
															color="cyan"
															className={SERVICE_AREA_LIST_CHIP_CLASS + ' cursor-pointer'}
														>
															{formatServiceAreaChipLabel(area.name)}
														</Tag>
													);
												})}

												{group.states.map((area) => {
													const areaIndex = serviceAreas.indexOf(area);
													return (
														<Tag
															key={'state-' + areaIndex}
															closable={!isReadOnly}
															onClose={isReadOnly ? undefined : () => handleRemove(areaIndex)}
															color="purple"
															onClick={() => handleSelectedChipClick(area)}
															className={SERVICE_AREA_LIST_CHIP_CLASS + ' cursor-pointer'}
														>
															{formatServiceAreaChipLabel(area.name)}
														</Tag>
													);
												})}

												{group.counties.map((area) => {
													const areaIndex = serviceAreas.indexOf(area);
													return (
														<Tag
															key={'county-' + areaIndex}
															closable={!isReadOnly}
															onClose={isReadOnly ? undefined : () => handleRemove(areaIndex)}
															onClick={() => handleSelectedChipClick(area)}
															color="geekblue"
															className={SERVICE_AREA_LIST_CHIP_CLASS + ' cursor-pointer'}
														>
															{formatServiceAreaChipLabel(area.name)}
														</Tag>
													);
												})}
											</div>
										</Collapse.Panel>
									);
								})}
							</Collapse>
						)}
					</div>
				</div>

				{/* Right panel — map */}
				<div className="flex min-w-0 flex-1 flex-col">
					{/* Top bar */}
					{!isReadOnly && (
						<div className="mb-2 flex min-w-0 shrink-0 items-center gap-2">
							<MapboxSearchInput
								onSelect={handleSearchSelect}
								geocodeTypes={SEARCH_GEOCODE_TYPES}
								placeholder="Search for a country, state, county, or ZIP / postal code..."
								value={searchText}
								onValueChange={setSearchText}
							/>
						</div>
					)}

					<div className="relative flex-1">
						{!isReadOnly && (
							<div className="absolute right-2.5 top-2.5 z-[11] flex items-center gap-1.5 rounded-md border border-grey-4 bg-white/95 px-2 py-1.5 shadow-[0_1px_4px_rgba(0,0,0,0.12)]">
								<span className="editSupplierServiceAreasForm__geoSelectLabel text-grey-8">
									Select:
								</span>
								<Radio.Group
									className="editSupplierServiceAreasForm__geoModeRadio"
									value={mode}
									onChange={(event) => {
										const selectedMode = event.target.value;
										setMode(selectedMode);
										setFocusedState(null);
									}}
									size="small"
									buttonStyle="solid"
								>
									<Radio.Button value="country">
										<span className="inline-flex items-center gap-1.5">
											<GeographyModeSwatch fillColor={SEL_COUNTRY_LINE} />
											Country
										</span>
									</Radio.Button>
									<Radio.Button value="region">
										<span className="inline-flex items-center gap-1.5">
											<GeographyModeSwatch fillColor={SEL_REGION_LINE} />
											State
										</span>
									</Radio.Button>
									<Radio.Button value="district">
										<span className="inline-flex items-center gap-1.5">
											<GeographyModeSwatch fillColor={SEL_DISTRICT_LINE} />
											County
										</span>
									</Radio.Button>
								</Radio.Group>
							</div>
						)}
						<ServiceAreaMap
							serviceAreas={serviceAreas}
							searchPreviewServiceArea={searchPreviewServiceArea}
							height="100%"
							geocodeTypes={mapGeocodeTypes}
							onMapClick={isReadOnly ? undefined : handleAdd}
							onApplyBoundaryRefinement={isReadOnly ? undefined : handleApplyBoundaryRefinement}
							onRemoveSelectedBoundary={isReadOnly ? undefined : handleRemoveBoundaryClick}
							centerOn={centerOn}
							boundarySelectionMode={isReadOnly ? undefined : mode}
							progressiveGeoBoundaries={true}
							showSelectionMarkers={false}
							onProgressiveMapReady={(mapInstance) => {
								progressiveMapRef.current = mapInstance;
							}}
						/>
					</div>
				</div>
			</div>

			{!isReadOnly && (
				<div className="flex shrink-0 justify-end gap-2 border-t border-grey-4 pt-4">
					<Button onClick={handleFooterCancelClick}>Cancel</Button>
					<Button type="primary" loading={isSavingServiceAreas || loading} onClick={handleSubmit}>
						Save
					</Button>
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	loading: state.supplier_facilities.updating,
	errors: state.supplier_facilities.updateErrors,
	successCallback: ownProps.successCallback,
	cancelCallback: ownProps.cancelCallback,
	supplierFacility: ownProps.supplierFacility,
});

const mapDispatchToProps = (dispatch) => ({
	updateSupplier: (supplierFacility) =>
		dispatch(supplierFacilitiesRestCrudThunksForSupplier.update(supplierFacility)),
	fetchSupplierFacility: (id) => dispatch(supplierFacilitiesRestCrudThunksForSupplier.readOne(id)),
	fetchServiceAreas: (supplierFacilityId) =>
		dispatch(fetchSupplierServiceAreas(supplierFacilityId)),
	updateServiceAreas: (supplierFacilityId, serviceAreas) =>
		dispatch(updateSupplierServiceAreas(supplierFacilityId, serviceAreas)),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(EditSupplierServiceAreasForm as any)
);
